import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { BASE_URL } from '../../api/apiClient';

// Change this if your Next.js app runs elsewhere (env var is cleaner for prod builds)
const FEMCARE_API_URL = `${BASE_URL}/api/femcare/`;

// Sorts by "order" field, falling back to original array position when missing.
function byOrder(items) {
  return [...items]
    .map((item, idx) => ({ item, idx }))
    .sort((a, b) => (a.item.order ?? a.idx) - (b.item.order ?? b.idx))
    .map(({ item }) => item);
}

/* ────────────────────────────────────────────────────────────────────────
 * Fallback content — used whenever the API call fails, returns a non-OK
 * status, or returns a payload with no usable navbar section. Shaped
 * identically to the live API response (logo / links / cta) so the render
 * logic below never has to branch on where the data came from.
 * ──────────────────────────────────────────────────────────────────────── */

const FALLBACK_NAVBAR = {
  logo: {
    src: 'https://fplogoimages.withfloats.com/actual/69307aedfe3cec81a85953aa.png',
    alt: 'Dada Gujar Hospital Logo',
  },
  links: [
    { id: 'home', label: 'Home', href: '/', status: 1, order: 0 },
    {
      id: 'howWeHelp',
      label: 'How We Help',
      href: '#',
      status: 1,
      order: 1,
      submenu: [
        { id: 'fc-1', label: 'Services', href: '/services', status: 1, order: 0 },
        { id: 'hwh-2', label: 'Life Stages', href: '/lifeStages', status: 1, order: 1 },
        { id: 'hwh-3', label: 'Conditions/Concerns', href: '/conditions', status: 1, order: 2 },
        { id: 'hwh-4', label: 'Programs', href: '/programs', status: 1, order: 3 },
      ],
    },
    { id: 'doctors', label: 'Doctors', href: '/our-doctors', status: 1, order: 2 },
    { id: 'resources', label: 'Resources', href: '/resource', status: 1, order: 3 },
    { id: 'about', label: 'About Us', href: '/about', status: 1, order: 4 },
  ],
  cta: {
    login: { label: 'Login', href: '/sign-in', status: 1, order: 0 },
    register: { label: 'Register', href: '/register', status: 1, order: 1 },
    carejourney: { label: 'Start Care Journey', href: '/carejourney', status: 1 },
  },
};

const Navbar = () => {
  const [navbar, setNavbar] = useState(null);
  const [loading, setLoading] = useState(true);
  // Tracks whether we're showing fallback content because the API call
  // failed or returned nothing usable. Not required for rendering — handy
  // if you want a subtle "offline" indicator later.
  const [usingFallback, setUsingFallback] = useState(false);

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null); // desktop submenu
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState(null); // mobile submenu
  const location = useLocation();
  const submenuRef = useRef(null);

  // Fetch live navbar content from the dashboard app. Falls back to
  // FALLBACK_NAVBAR whenever the request fails, the response isn't ok, or
  // the payload has no usable navbar data — the nav should never just
  // vanish because the CMS is down.
  useEffect(() => {
    setNavbar(FALLBACK_NAVBAR);
    setUsingFallback(true);
    setLoading(false);
    // let cancelled = false;

    // fetch(FEMCARE_API_URL)
    //   .then((res) => {
    //     if (!res.ok) throw new Error(`Failed to fetch navbar: ${res.status}`);
    //     return res.json();
    //   })
    //   .then((data) => {
    //     if (cancelled) return;

    //     const fetchedNavbar = data?.navbar;
    //     if (!fetchedNavbar || !Array.isArray(fetchedNavbar.links) || fetchedNavbar.links.length === 0) {
    //       throw new Error('Navbar payload missing or empty');
    //     }

    //     setNavbar(fetchedNavbar);
    //     setUsingFallback(false);
    //   })
    //   .catch((err) => {
    //     console.error('Navbar: falling back to default content —', err.message);
    //     if (!cancelled) {
    //       setNavbar(FALLBACK_NAVBAR);
    //       setUsingFallback(true);
    //     }
    //   })
    //   .finally(() => {
    //     if (!cancelled) setLoading(false);
    //   });

    // return () => {
    //   cancelled = true;
    // };
  }, []);

 const getActiveId = (links) => {
  const pathname = location.pathname.toLowerCase().replace(/\/$/, '');
  const hash = location.hash.toLowerCase();

  // Pass 1: links that include a hash (e.g. "/about#contact") must match
  // BOTH the path and the hash exactly. Checked first so a hash-specific
  // link like "Contact Us" wins over a plain sibling like "About Us"
  // ("/about") when both are on the same page.
  for (const link of links) {
    if (link.href.includes('#') && link.href.startsWith('/')) {
      const [hrefPath, hrefHash] = link.href.toLowerCase().split('#');
      const normalizedHrefPath = hrefPath.replace(/\/$/, '');
      if (pathname === normalizedHrefPath && hash === `#${hrefHash}`) {
        return link.id;
      }
    }
  }

  // Pass 2: plain path-only links (no hash), same as before.
  for (const link of links) {
    if (link.href.startsWith('/') && link.href !== '/' && !link.href.includes('#')) {
      const normalizedHref = link.href.toLowerCase().replace(/\/$/, '');
      if (pathname === normalizedHref) return link.id;
    }
    if (link.submenu) {
      for (const sub of link.submenu) {
        if (sub.href.startsWith('/') && !sub.href.includes('#') && pathname === sub.href.toLowerCase().replace(/\/$/, '')) {
          return link.id;
        }
      }
    }
  }

  // Pass 3: hash-only fallback (unchanged from before), for links whose
  // href is purely a hash like "#contact" with no leading path.
  if (hash) {
    for (const link of links) {
      if (link.href === hash) return link.id;
    }
  }

  if (pathname === '' || pathname === '/') return 'home';
  return '';
};

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close drawer on desktop resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) setMenuOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-close drawer on route change
  useEffect(() => {
    setMenuOpen(false);
    setOpenSubmenu(null);
    setMobileSubmenuOpen(null);
  }, [location.pathname]);

  // Lock body scroll when drawer open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  const toggleMobileSubmenu = (linkId) => {
    setMobileSubmenuOpen((prev) => (prev === linkId ? null : linkId));
  };

  // ── Loading guard only ────────────────────────────────────────────────
  // Once the fetch settles (success or failure), `navbar` is always
  // populated — either from the API or from FALLBACK_NAVBAR — so there's
  // no more "error or !navbar → render nothing" branch. The nav bar
  // should never disappear just because the CMS is unreachable.
  if (loading || !navbar) {
    return (
      <nav className="bg-white w-full sticky top-0 z-50 shadow-sm h-16 md:h-18" />
    );
  }

  // ── Apply saved ordering AND visibility — single source of truth for both
  // desktop and mobile, so they never drift out of sync with each other. ──
  const visibleLinks = byOrder(
    (navbar.links || []).filter((link) => link.status === 1)
  ).map((link) => ({
    ...link,
    submenu: byOrder(
      (link.submenu || []).filter((sub) => sub.status === 1)
    ),
  }));

  const sortedLoginRegister = byOrder(
    [navbar.cta.login, navbar.cta.register].filter(Boolean)
  );
  // Keep a lookup so we can still reference by key where needed (e.g. mobile drawer order).
  const loginRegisterPair = sortedLoginRegister; // [first, second] in saved order

  const activeId = getActiveId(visibleLinks);
  const isActive = (link) => activeId === link.id;

  return (
    <nav
      className={`bg-white w-full bg-white sticky top-0 z-50 transition-shadow duration-300 ${
        scrolled ? 'shadow-md' : 'shadow-sm'
      }`}
    >
      <div className=" px-4 md:px-8">
        <div className="flex items-center justify-between h-18 md:h-20">

          {/* Logo */}
          <a href="/" className="flex items-center gap-3 flex-shrink-0">
            <img
              src={navbar.logo?.src || '/Home/logo.svg'}
              alt={navbar.logo?.alt || 'Logo'}
              className="h-10 md:h-16 w-auto"
            />
          </a>
          {navbar.cta.carejourney?.status==1 && (
          <div className='hidden lg:flex items-center'>
            <a href={navbar.cta.carejourney?.href}
            className='btn-transparent'
            style={{color:'var(--primary-color)', borderColor:'var(--primary-color)'}}
            >
              {navbar.cta.carejourney?.label}
            </a>
          </div>) }

          {/* Desktop Nav Links */}
          <ul
            ref={submenuRef}
            className="hidden lg:flex items-center gap-1 xl:gap-2"
          >
            {visibleLinks.map((link) => (
              <li
                key={link.id}
                className="relative"
                onMouseEnter={() => link.submenu?.length > 0 && setOpenSubmenu(link.id)}
                onMouseLeave={() => link.submenu?.length > 0 && setOpenSubmenu(null)}
                style={link.submenu?.length > 0 ? { paddingBottom: '8px', marginBottom: '-8px' } : {}}
              >
                {link.submenu?.length > 0 ? (
                  /* ── Link with submenu ── */
                  <>
                    <button
                      className="px-3 xl:px-3 ava-text-sm rounded-md transition-colors duration-200 flex flex-col items-center focus:outline-none"
                      style={isActive(link) ? { color: 'var(--primary-color)' } : {}}
                      aria-expanded={openSubmenu === link.id}
                      aria-haspopup="true"
                    >
                      <span className="flex items-center gap-1">
                        <span className={isActive(link) ? 'font-semibold' : 'text-gray-700'}>
                          {link.label}
                        </span>
                      </span>
                      {isActive(link) && (
                        <span
                          className="block h-0.5 w-full mt-0.5"
                          style={{ backgroundColor: 'var(--primary-color)' }}
                        />
                      )}
                    </button>

                    {/* Dropdown panel */}
                    {openSubmenu === link.id && (
                      <div
                        className="absolute top-full left-0 min-w-56 rounded-xl overflow-hidden z-50"
                        style={{
                          backgroundColor: '#fff',
                          boxShadow: '0 8px 32px rgba(0,36,107,0.13)',
                          border: '1px solid var(--gray-200)',
                        }}
                      >
                        {link.submenu.some((s) => s.group) && (
                          <div className="flex flex-col">
                            {groupBy(link.submenu).map((group) => (
                              <div key={group.label}>
                                {group.label && (
                                  <p
                                    className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider"
                                    style={{ color: 'var(--text-light)' }}
                                  >
                                    {group.label}
                                  </p>
                                )}
                                {group.items.map((sub) => (
                                  <SubmenuItem
                                    key={sub.id}
                                    sub={sub}
                                    onClick={() => setOpenSubmenu(null)}
                                  />
                                ))}
                              </div>
                            ))}
                          </div>
                        )}

                        {!link.submenu.some((s) => s.group) && (
                          <div className="py-1.5">
                            {link.submenu.map((sub) => (
                              <SubmenuItem
                                key={sub.id}
                                sub={sub}
                                onClick={() => setOpenSubmenu(null)}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  /* ── Regular link ── */
                  
                    <a href={link.href}
                    className="px-3 xl:px-3 ava-text-sm rounded-md transition-colors duration-200 flex flex-col items-center"
                    style={isActive(link) ? { color: 'var(--primary-color)' } : {}}
                  >
                    <span className={isActive(link) ? 'font-semibold' : 'text-gray-700'}>
                      {link.label}
                    </span>
                    {isActive(link) && (
                      <span
                        className="block h-0.5 w-full mt-0.5"
                        style={{ backgroundColor: 'var(--primary-color)' }}
                      />
                    )}
                  </a>
                )}
              </li>
            ))}
          </ul>

          {/* Desktop CTA Buttons (order-aware: login/register swap per saved order) */}
          <div className="hidden lg:flex items-center gap-3">
            {loginRegisterPair.map((cta, idx) => {
              const isLogin = cta === navbar.cta.login;
              return (
                
                <a  key={isLogin ? 'login' : 'register'}
                  href={cta?.href}
                  className={isLogin ? 'btn-transparent' : 'btn-secondary'}
                  style={isLogin ? { color: 'var(--primary-color)', borderColor: 'var(--primary-color)' } : {}}
                >
                  {cta?.label}
                </a>
              );
            })}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="lg:hidden flex flex-col justify-center items-center w-10 h-10 rounded-md hover:bg-gray-100 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
            aria-expanded={menuOpen}
          >
            <span className={`block w-5 h-0.5 bg-gray-700 transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-1.5' : ''}`} />
            <span className={`block w-5 h-0.5 bg-gray-700 my-1 transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-5 h-0.5 bg-gray-700 transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-1.5' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-40 z-40 lg:hidden"
          onClick={() => setMenuOpen(false)}
        />
      )}

      {/* Mobile Drawer */}
      <div
        className={`lg:hidden fixed top-0 right-0 h-full w-72 bg-white z-50 shadow-2xl transform transition-transform duration-300 ease-in-out ${
          menuOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: 'var(--gray-200)' }}>
          <img src={navbar.logo?.src || '/Home/logo.svg'} alt="Logo" className="h-9 w-auto" />
          <button
            onClick={() => setMenuOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <ul className="flex flex-col py-4 overflow-y-auto">
          {visibleLinks.map((link) => (
            <li key={link.id}>
              {link.submenu?.length > 0 ? (
                <>
                  <button
                    onClick={() => toggleMobileSubmenu(link.id)}
                    className="w-full flex items-center justify-between px-6 py-3.5 ava-text-sm font-medium transition-colors duration-200 border-l-4"
                    style={
                      isActive(link)
                        ? { color: 'var(--primary-color)', backgroundColor: 'var(--blue)', borderColor: 'var(--primary-color)', fontWeight: 600 }
                        : { color: '#374151', borderColor: 'transparent' }
                    }
                  >
                    <span>{link.label}</span>
                    <svg
                      className="w-4 h-4 transition-transform duration-200 flex-shrink-0"
                      style={{
                        transform: mobileSubmenuOpen === link.id ? 'rotate(180deg)' : 'rotate(0deg)',
                        color: isActive(link) ? 'var(--primary-color)' : '#6b7280',
                      }}
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  <div
                    className="overflow-hidden transition-all duration-300"
                    style={{ maxHeight: mobileSubmenuOpen === link.id ? `${link.submenu.length * 52}px` : '0px' }}
                  >
                    {link.submenu.map((sub) => (
                      
                       <a key={sub.id}
                        href={sub.href}
                        onClick={() => setMenuOpen(false)}
                        className="flex items-center gap-3 pl-10 pr-6 py-3 ava-text-sm transition-colors duration-200 border-l-4 hover:bg-gray-50"
                        style={{ color: '#374151', borderColor: 'transparent' }}
                      >
                        {sub.icon && (
                          <img src={sub.icon} alt="" className="w-4 h-4 flex-shrink-0" style={{ opacity: 0.6 }} />
                        )}
                        <div>
                          <p className="font-medium ava-text-sm" style={{ color: 'var(--text-dark)' }}>{sub.label}</p>
                          {sub.description && (
                            <p className="ava-text-xs" style={{ color: 'var(--text-light)' }}>{sub.description}</p>
                          )}
                        </div>
                      </a>
                    ))}
                  </div>
                </>
              ) : (
                
                 <a href={link.href}
                  className="flex items-center px-6 py-3.5 ava-text-sm font-medium transition-colors duration-200 border-l-4"
                  style={
                    isActive(link)
                      ? { color: 'var(--primary-color)', backgroundColor: 'var(--blue)', borderColor: 'var(--primary-color)', fontWeight: 600 }
                      : { color: '#374151', borderColor: 'transparent' }
                  }
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </a>
              )}
            </li>
          ))}
        </ul>

        {/* Drawer CTA (order-aware) */}
        <div className="px-5 py-4 border-t flex flex-col gap-3" style={{ borderColor: 'var(--gray-200)' }}>
          {loginRegisterPair.map((cta) => {
            const isLogin = cta === navbar.cta.login;
            return (
              
               <a key={isLogin ? 'login' : 'register'}
                href={cta?.href}
                onClick={() => setMenuOpen(false)}
                className={
                  isLogin
                    ? 'w-full text-center px-5 py-2.5 ava-text-sm font-semibold rounded-md border-2 transition-all duration-200 hover:bg-gray-50'
                    : 'btn-secondary w-full text-center py-2.5 rounded-md'
                }
                style={isLogin ? { color: 'var(--primary-color)', borderColor: 'var(--primary-color)' } : {}}
              >
                {cta?.label}
              </a>
            );
          })}
        </div>
      </div>
    </nav>
  );
};

/* ── Desktop submenu item ── */
const SubmenuItem = ({ sub, onClick }) => (
  
    <a href={sub.href}
    onClick={onClick}
    className="flex items-start gap-3 px-4 py-2.5 transition-colors duration-150 hover:bg-gray-50 group"
  >
    {sub.icon && (
      <div
        className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5"
        style={{ backgroundColor: 'var(--blue)' }}
      >
        <img src={sub.icon} alt="" className="w-4 h-4" />
      </div>
    )}
    <div className="flex flex-col min-w-0">
      <span
        className="ava-text-sm font-medium "
        style={{ color: 'var(--primary-color)' }}
      >
        {sub.label}
      </span>
      {sub.description && (
        <span
          className="ava-text-xs mt-0.5"
          style={{ color: 'var(--text-light)' }}
        >
          {sub.description}
        </span>
      )}
    </div>
  </a>
);

/* ── Group submenu items by group label ── */
const groupBy = (items) => {
  const groups = [];
  const seen = {};
  for (const item of items) {
    const g = item.group || '';
    if (!seen[g]) {
      seen[g] = { label: g, items: [] };
      groups.push(seen[g]);
    }
    seen[g].items.push(item);
  }
  return groups;
};

export default Navbar;